import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { analytics, logEvent } from "./Components/firebase";
import "./App.css";
import DashboardLayout from "./Components/DashboardLayout";
import DashboardLayout2 from "./Allview/DashboardLayout2";
import GoogleLogin from "./Components/GoogleLogin";
import ParentComponent2 from './TeamView/ParentComponent2'; // ✅ Adjust if path differs
import StateView from './StateView/ParentComponent'
import DefinitionPage from './StateView/DefinitionPage';
import DefinitionPage1 from './Components/DefinitionPage1';
import DefinitionPage2 from './Allview/DefinitionPage2';
import { setUserId, setUserProperties } from "firebase/analytics";

function TrackPageView() {
  const location = useLocation();

  useEffect(() => {
    logEvent(analytics, "page_view", { page: location.pathname });
  }, [location]);

  return null;
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
  const FORCE_LOGOUT_LIMIT = 2 * 60 * 60 * 1000; // 2 hours
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);



  useEffect(() => {
    axios.get("https://api-salesadmin.haqdarshak.com/api/users/achievement-dashboard")
      .then(response => {
        if (response.data && Array.isArray(response.data)) {
          setAuthorizedUsers(response.data);
        }
      })
      .catch(error => console.error("Error fetching authorized users:", error));
  }, []);

  useEffect(() => {
    const storedLogin = localStorage.getItem("isLoggedIn") === "true";
    const storedUserDetails = localStorage.getItem("userDetails");
    const storedAuthorization = localStorage.getItem("isAuthorized") === "true";
    const sessionStart = localStorage.getItem("sessionStart");

    setIsLoggedIn(storedLogin);
    setIsAuthorized(storedAuthorization);
    if (storedUserDetails) {
      setUserDetails(JSON.parse(storedUserDetails));
    }

    if (storedLogin && sessionStart) {
      const sessionStartTime = new Date(sessionStart).getTime();
      const currentTime = new Date().getTime();
      if (currentTime - sessionStartTime >= FORCE_LOGOUT_LIMIT) {
        handleLogout();
      }
    }

  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      let inactivityTimer;
      let forceLogoutTimer;

      const resetInactivityTimer = () => {
        clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(() => {
          console.log("User inactive for 15 minutes. Logging out...");
          handleLogout();
        }, INACTIVITY_LIMIT);
      };

      const startForceLogoutTimer = () => {
        forceLogoutTimer = setTimeout(() => {
          console.log("2-hour session reached. Logging out...");
          handleLogout();
        }, FORCE_LOGOUT_LIMIT);
      };

      document.addEventListener("mousemove", resetInactivityTimer);
      document.addEventListener("keydown", resetInactivityTimer);
      document.addEventListener("click", resetInactivityTimer);

      resetInactivityTimer();
      startForceLogoutTimer();

      return () => {
        clearTimeout(inactivityTimer);
        clearTimeout(forceLogoutTimer);
        document.removeEventListener("mousemove", resetInactivityTimer);
        document.removeEventListener("keydown", resetInactivityTimer);
        document.removeEventListener("click", resetInactivityTimer);
      };
    }
  }, [isLoggedIn]);

  const handleGoogleSuccess = (response) => {
    try {
      const decoded = jwtDecode(response.credential);
      const userEmail = decoded.email;
      const userName = decoded.name;
      const authorized = authorizedUsers.includes(userEmail);

      setIsLoggedIn(true);
      setUserDetails(decoded);
      setIsAuthorized(authorized);
      setShowWelcomeModal(true);

      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("userDetails", JSON.stringify(decoded));
      localStorage.setItem("isAuthorized", authorized ? "true" : "false");
      localStorage.setItem("sessionStart", new Date().toISOString());

      setUserId(analytics, userEmail);
      setUserProperties(analytics, { email: userEmail, user_name: userName });

      logEvent(analytics, "user_login", { method: "Google SSO", email: userEmail, user_name: userName });

      console.log(`User logged in: ${userEmail}`);

    } catch (error) {
      console.error("Failed to decode Google credential:", error);
    }
  };

  const handleLogout = () => {
    if (userDetails) {
      const userEmail = userDetails.email;
      const sessionStart = localStorage.getItem("sessionStart");
      const sessionEnd = new Date().toISOString();

      let sessionDuration = 0;
      if (sessionStart) {
        sessionDuration = Math.floor((new Date(sessionEnd) - new Date(sessionStart)) / 1000);
      }

      logEvent(analytics, "user_logout", { 
        email: userEmail, 
        name: userDetails.name,
        sessionEndTime: sessionEnd, 
        sessionDuration: sessionDuration 
      });

      console.log(`User ${userEmail} logged out. Session Duration: ${sessionDuration} seconds`);
    }

    localStorage.clear();
    sessionStorage.clear();
    setIsLoggedIn(false);
    setIsAuthorized(false);
    setUserDetails(null);
  };

  return (
    <GoogleOAuthProvider clientId="442497047534-nu53j4qreppmcie4qvjd04cpbefjlks7.apps.googleusercontent.com">
      <Router>
        <TrackPageView />


        {showWelcomeModal && (
  <div className="welcome-modal-overlay">
    <div className="welcome-modal">
      <button className="close-btn" onClick={() => setShowWelcomeModal(false)}>×</button>
      <h2>Welcome!</h2>
      <p className="modal-intro">Here's what you can do in this dashboard:</p>

      <div className="modal-flow">
        <div className="flow-step">
          <div className="circle-number">1</div>
          <div className="flow-box">
            <div className="box-title">Project Achievement View</div>
            <div className="box-desc">
              View target vs achievement for each deliverable, including state-wise progress and how many employees are deployed per project.
            </div>
          </div>
        </div>

        <div className="arrow-symbol">➔</div>

        <div className="flow-step">
          <div className="circle-number">2</div>
          <div className="flow-box">
            <div className="box-title">Summary in Tables</div>
            <div className="box-desc">
              Track application-related summary (DI, DFL, YC) across employees, broken down by day, week, and month.
            </div>
          </div>
        </div>

        <div className="arrow-symbol">➔</div>

        <div className="flow-step">
          <div className="circle-number">3</div>
          <div className="flow-box">
            <div className="box-title">Individual Performance</div>
            <div className="box-desc">
              Dive into manager-level performance, check timeliness, and compare across multiple parameters.
            </div>
          </div>
        </div>

        <div className="arrow-symbol">➔</div>

        <div className="flow-step">
          <div className="circle-number">4</div>
          <div className="flow-box">
            <div className="box-title">Team View</div>
            <div className="box-desc">
              Assess team-wise performance under each manager to evaluate overall contribution.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}






        <div className="app">
          {!isLoggedIn ? (
            <GoogleLogin
              onLoginSuccess={handleGoogleSuccess}
              onLoginFailure={() => console.error("Login Failed")}
            />
          ) : isAuthorized ? (

            

            <Routes>
            
            <Route path="/" element={<StateView onLogout={handleLogout} />} />         
            <Route path="/all-view" element={<DashboardLayout2 onLogout={handleLogout} />} />          
            <Route path="/summary" element={<DashboardLayout onLogout={handleLogout} />} />
            <Route path="/team-view" element={<ParentComponent2 onLogout={handleLogout} />} />
            <Route path="/state-view" element={<StateView onLogout={handleLogout} />} />
            <Route path="/definition" element={<DefinitionPage />} />
            <Route path="/definition1" element={<DefinitionPage1 />} />
            <Route path="/definition2" element={<DefinitionPage2 />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          
          ) : (
            
            <div className="access-denied">
              <h2>Access Denied</h2>
              <p>You do not have access to this dashboard. Please log out.</p>
              <p>For access, please reach out to the Data Team at data@haqdarshak.com</p>
              <button 
                className="logout-btn" 
                onClick={() => {
                  handleLogout();
                  logEvent(analytics, "unauthorized_access_attempt", { email: userDetails?.email || "unknown" });
                }}
              >
                Logout & Retry
              </button>
            </div>
          )}
        </div>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
